import { AuthApplication } from '@/application/auth/auth';
import type { TKeypairParams } from '@/application/auth/auth.types';
import { BootstrapApplication, type BootstrapProgressCallback } from '@/application/bootstrap/bootstrap';
import { SettingsApplication } from '@/application/settings/settings';
import { postStreamQueue } from '@/application/stream/posts/muting/post-stream-queue';
import { UserApplication } from '@/application/user/user';
import { getModerationId } from '@/config/moderation';
import type {
  TLoginWithEncryptedFileParams,
  TLoginWithMnemonicParams,
  TSignUpParams,
} from '@/controllers/auth/auth.types';
import { LocksController } from '@/controllers/locks/locks';
import { captureViewerSession } from '@/controllers/tag/tag-cache.utils';
import { NotificationCoordinator } from '@/coordinators/notifications/notifications';
import { StreamCoordinator } from '@/coordinators/streams/stream';
import { clearDatabase } from '@/database/franky/franky.helpers';
import { createCanceledError } from '@/libs/error/auth-flow-canceled';
import { ErrorService } from '@/libs/error/error.types';
import { isAppError, isWrongEnvironmentHomeserverError, toAppError } from '@/libs/error/error.utils';
import { Identity } from '@/libs/identity/identity';
import { Logger } from '@/libs/logger/logger';
import { clearMuteSyncCursorSessionStorage } from '@/libs/mute-sync/clear-cursor-session-storage';
import { clearAllQueryClients } from '@/libs/query-client/query-client.factory';
import { clearCookies, sleep } from '@/libs/utils/utils';
import type { Pubky } from '@/models/models.types';
import { NotificationNormalizer } from '@/pipes/notification/notification.normalizer';
import { PubkySpecsSingleton } from '@/pipes/pipes.builder';
import { SettingsNormalizer } from '@/pipes/settings/settings.normalizer';
import type { TGenerateAuthUrlResult, THomeserverSessionResult } from '@/services/homeserver/homeserver.types';
import { useAuthStore } from '@/stores/auth/auth.store';
import { useHomeStore } from '@/stores/home/home.store';
import { useHotStore } from '@/stores/hot/hot.store';
import { useLocalFilesStore } from '@/stores/localFiles/localFiles.store';
import { useMigrationStore } from '@/stores/migration/migration.store';
import { useNotificationStore } from '@/stores/notification/notification.store';
import { useOnboardingStore } from '@/stores/onboarding/onboarding.store';
import { useSearchStore } from '@/stores/search/search.store';
import { useSettingsStore } from '@/stores/settings/settings.store';
import type { SettingsState } from '@/stores/settings/settings.types';
import { useSignInStore } from '@/stores/signIn/signIn.store';

/**
 * How long logout waits for the homeserver to end the session. The SDK's `session.signout()`
 * accepts no timeout or AbortSignal, so a homeserver that connects but never replies would
 * otherwise hold logout open for the OS-level TCP timeout. Local cleanup runs either way.
 */
const LOGOUT_TIMEOUT_MS = 5_000;

export class AuthController {
  private constructor() {} // Prevent instantiation

  private static activeAuthFlow: { token: symbol; cancel: (() => void) | null } | null = null;
  private static passportSessionPending: THomeserverSessionResult['session'] | null = null;
  private static moderationFollowAbortController: AbortController | null = null;

  static cancelActiveAuthFlow() {
    const cancel = this.activeAuthFlow?.cancel;
    this.activeAuthFlow = null;
    cancel?.();
  }

  /** True while `token` identifies the flow that currently owns auth-flow state. */
  private static ownsAuthFlow(token: symbol): boolean {
    return this.activeAuthFlow?.token === token;
  }

  /** Cancel detached moderation-follow work before account-local state changes ownership. */
  static cancelModerationFollow(): void {
    this.moderationFollowAbortController?.abort();
    this.moderationFollowAbortController = null;
  }

  private static clearModerationFollowController(controller: AbortController): void {
    if (this.moderationFollowAbortController === controller) {
      this.moderationFollowAbortController = null;
    }
  }

  /** Start settings-aware moderation follow work without extending the authentication critical path. */
  private static startModerationFollow(pubky: Pubky): void {
    this.cancelModerationFollow();
    const controller = new AbortController();
    this.moderationFollowAbortController = controller;
    void this.ensureModerationFollow(pubky, controller);
  }

  private static async ensureModerationFollow(pubky: Pubky, controller: AbortController): Promise<void> {
    try {
      const moderationId = await UserApplication.ensureModerationFollow({
        follower: pubky,
        moderationId: getModerationId(),
        moderationBot: useSettingsStore.getState().privacy.moderationBot,
        signal: controller.signal,
      });

      // The settings store is account-local; never record processed state against a different session.
      if (!moderationId || controller.signal.aborted || useAuthStore.getState().currentUserPubky !== pubky) return;

      useSettingsStore.getState().setModerationBot(moderationId);
      const settings = SettingsNormalizer.extractState(useSettingsStore.getState());
      await SettingsApplication.commitUpdate(settings, pubky, controller.signal);
    } catch (error) {
      // AppError factories already report at their origin. Only unexpected raw failures need a warning.
      if (!controller.signal.aborted && !isAppError(error)) {
        Logger.warn('Unexpected moderation-follow authentication failure', { error });
      }
    } finally {
      this.clearModerationFollowController(controller);
    }
  }

  /**
   * Restores a persisted session from the auth store.
   * @returns true on success, false on failure
   * @throws Wrong-environment homeserver errors after local cleanup so UI can show feedback
   */
  static async restorePersistedSession(): Promise<boolean> {
    this.cancelModerationFollow();
    const authStore = useAuthStore.getState();
    let isCurrent = captureViewerSession();
    try {
      const result = await AuthApplication.restorePersistedSession({ authStore });
      if (!isCurrent() || useAuthStore.getState().isLoggingOut) return false;

      if (!result) {
        await this.cleanupLocalState();
        return false;
      }
      const { session } = result;
      const currentUserPubky = Identity.z32FromSession({ session });
      // A session restored from a persisted export can carry an undetermined profile: reloading
      // mid sign-in persists `hasProfile: null` before the profile check completes. Leaving it
      // unknown read as unauthenticated, which rendered the landing page behind the signed-in
      // header (issue #2070).
      const hasProfile = authStore.hasProfile;
      authStore.init({ session, currentUserPubky, hasProfile });
      isCurrent = captureViewerSession();

      if (hasProfile === null && !(await this.resolveRestoredProfileState({ pubky: currentUserPubky }))) {
        if (isCurrent() && !useAuthStore.getState().isLoggingOut) await this.cleanupLocalState();
        return false;
      }

      return true;
    } catch (error) {
      if (!isCurrent() || useAuthStore.getState().isLoggingOut) return false;
      const appError = toAppError(error, ErrorService.Local, 'restorePersistedSession');
      await this.cleanupLocalState();
      if (isWrongEnvironmentHomeserverError(appError)) {
        throw appError;
      }
      return false;
    }
  }

  /**
   * Resolves the profile state of a session restored from a persisted export, keeping
   * `hasProfile` unknown until the homeserver answers and sign-in initialization completes.
   *
   * While this runs, useAuthStatus keeps the app loading, so no route decides on an
   * undetermined profile. A state that stays undetermined after the retries signs the session
   * out instead of letting the app treat the restored account as signed out (issue #2070).
   *
   * @param params - Parameters containing the restored session's public key
   * @param params.pubky - The restored session's public key identifier
   * @returns true when the store now holds a definite profile state
   */
  private static async resolveRestoredProfileState({ pubky }: { pubky: Pubky }): Promise<boolean> {
    const isCurrent = captureViewerSession();
    useAuthStore.getState().setIsResolvingProfile(true);
    try {
      const hasProfile = await AuthApplication.resolveUserIsSignedUp({ pubky });
      if (!isCurrent() || useAuthStore.getState().isLoggingOut || hasProfile === null) return false;

      // Reloading during sign-in can interrupt settings sync and bootstrap after the session
      // export was saved. Resume that work before authenticated routes become accessible.
      if (hasProfile) await this.hydrateMeImAlive({ pubky });
      if (!isCurrent() || useAuthStore.getState().isLoggingOut) return false;

      useAuthStore.getState().setHasProfile(hasProfile);
      return true;
    } finally {
      if (isCurrent()) useAuthStore.getState().setIsResolvingProfile(false);
    }
  }

  /**
   * Gets a homeserver service instance using the secret key from the onboarding store.
   * @param params - The authentication parameters
   * @param params.keypair - The cryptographic keypair for the user
   * @returns Configured homeserver service instance
   */
  private static async signIn({ keypair }: TKeypairParams): Promise<boolean> {
    this.cancelModerationFollow();
    // Clear query clients to ensure no stale cache from previous session
    clearAllQueryClients();
    // Clear database before sign in to ensure clean state
    await clearDatabase();
    // Skip post-migration resync — bootstrap runs if user has profile, otherwise no data to resync
    useMigrationStore.getState().reset();
    // Settings are account-local: start from defaults so a previous account's document is never pushed to this one
    useSettingsStore.getState().reset();
    const session = await AuthApplication.signIn({ keypair });
    if (!session) {
      Logger.error('Failed to sign in. Please try again.', { keypair });
      return false;
    }
    // Environment guard already ran inside HomeserverService.signIn (before the
    // session was created), so go straight to shared initialization.
    await this.completeAuthenticatedSession(session);
    return true;
  }

  /**
   * Bootstrap data to initialize the application snapshot.
   * @param params - Object containing session and pubky data from authentication
   * @param params.session - The user session data
   * @param params.pubky - The user's public key identifier
   */
  private static async hydrateMeImAlive({ pubky }: { pubky: Pubky }) {
    const isCurrent = captureViewerSession();
    const signInStore = useSignInStore.getState();
    const {
      meta: { url },
    } = NotificationNormalizer.to(pubky);

    // Progress callback to update signInStore from Controller layer (respecting architecture rules)
    const onProgress: BootstrapProgressCallback = (step) => {
      if (!isCurrent()) return;
      switch (step) {
        case 'bootstrapFetched':
          signInStore.setBootstrapFetched(true); // Step 3 complete (60%)
          break;
        case 'dataPersisted':
          signInStore.setDataPersisted(true); // Step 4 complete (80%)
          break;
        case 'homeserverSynced':
          signInStore.setHomeserverSynced(true); // Step 5 complete (100%)
          break;
      }
    };

    const localSettings = SettingsNormalizer.extractState(useSettingsStore.getState());

    // Sync settings before bootstrap; failures fall back locally and suppress default-follow work for this session.
    const { remoteSettings, succeeded: settingsSyncSucceeded } = await this.syncSettings(pubky, localSettings);

    // Resolve final preferences: remote settings win if available, otherwise use local
    const preferences = (remoteSettings ?? localSettings).notifications;
    const allowedTypes = NotificationNormalizer.toEnabledTypes(preferences);

    if (!isCurrent()) return;
    const notification = await BootstrapApplication.initialize(
      { pubky, lastReadUrl: url, allowedTypes, isCurrent },
      onProgress,
    );
    if (!isCurrent()) return;
    useNotificationStore.getState().setState(notification);

    // Apply remote settings to store (store mutation stays in Controller layer)
    if (remoteSettings) {
      useSettingsStore.getState().loadFromHomeserver(remoteSettings);
      Logger.info('Settings loaded from homeserver', { pubky });
    }

    if (settingsSyncSucceeded) {
      this.startModerationFollow(pubky);
    }
  }

  /**
   * Initializes the authenticated session and checks if the user is signed up (profile.json in homeserver).
   *
   * Runs the staging environment guard first: the session was approved externally
   * (e.g. Pubky Ring), so this is its only checkpoint. Keypair flows skip the
   * guard here — HomeserverService.signIn already ran it before creating the
   * session, and re-asserting would duplicate the PKARR lookup and let a
   * transient second lookup abort an already-verified sign-in.
   * @param params - Object containing session data from authentication
   * @param params.session - The user session data
   */
  static async initializeAuthenticatedSession({ session }: THomeserverSessionResult) {
    try {
      await AuthApplication.assertUserHomeserverAllowed({ publicKey: session.info.publicKey });
    } catch (error) {
      // The just-approved session lives on the user's actual homeserver — sign it
      // out instead of leaving it dangling, whether the key was rejected or the
      // lookup failed. Best-effort: the failure must surface regardless.
      await AuthApplication.logout({ session }).catch((logoutError) => {
        Logger.warn('Failed to sign out session after environment check failure', { logoutError });
      });
      throw error;
    }
    await this.completeAuthenticatedSession({ session });
  }

  /** Accept a Passport grant only once; the package has already required a valid profile. */
  static async initializePassportSession({
    session,
    passportInstance,
  }: THomeserverSessionResult & { passportInstance: string }): Promise<boolean> {
    const previous = useAuthStore.getState();
    if (this.passportSessionPending || (previous.session && previous.hasProfile !== false)) {
      if (session !== useAuthStore.getState().session && session !== this.passportSessionPending) {
        await AuthApplication.logout({ session }).catch(() => undefined);
        session.free();
      }
      return false;
    }
    this.passportSessionPending = session;
    let storedSessionId: string | null = null;
    try {
      this.cancelActiveAuthFlow();
      this.cancelModerationFollow();
      clearAllQueryClients();
      if (previous.session) await AuthApplication.logout({ session: previous.session }).catch(() => undefined);
      if (previous.storedSessionId)
        await AuthApplication.removeStoredSession(previous.storedSessionId).catch(() => undefined);
      useAuthStore.getState().reset();
      await clearDatabase();
      useMigrationStore.getState().reset();
      useSettingsStore.getState().reset();
      useOnboardingStore.getState().reset();
      await AuthApplication.assertUserHomeserverAllowed({ publicKey: session.info.publicKey });
      storedSessionId = await AuthApplication.saveSession(session);
      await this.completeAuthenticatedSession({ session }, storedSessionId, passportInstance);
      return true;
    } catch (error) {
      if (storedSessionId) await AuthApplication.removeStoredSession(storedSessionId).catch(() => undefined);
      await AuthApplication.logout({ session }).catch(() => undefined);
      session.free();
      throw error;
    } finally {
      this.passportSessionPending = null;
    }
  }

  /**
   * Swaps the stored session for one the user just approved with today's capability list (#2373).
   * Not a sign-in: no profile check, bootstrap or sign-in progress, so the route guard sees no change
   * and the user keeps their place. The old session is never signed out — the homeserver keys its
   * cookie by pubky, so that would drop the new session as well. Guards that compare the session
   * object (`captureViewerSession`, the TTL coordinator) see one change: reads in flight are dropped
   * once and TTL restarts, both of which the next interaction recovers from.
   *
   * @returns false when the approval came from another key — a user picking the wrong identity in
   * Pubky Ring, which the caller reports as a toast. Not an `Err.*`: it is an expected choice, and
   * an AppError would file it in Sentry as a fault.
   */
  static async upgradeSession({ session }: THomeserverSessionResult): Promise<boolean> {
    // No `cancelActiveAuthFlow` here: the flow that produced this approval already cancelled itself
    // on settle, so the only flow left to cancel would be a newer one the user just started — whose
    // QR would then stop polling and never register their next approval.
    const authStore = useAuthStore.getState();
    const pubky = Identity.z32FromSession({ session });

    if (pubky !== authStore.currentUserPubky) {
      // That session is real on its own homeserver, so end it instead of leaving it dangling.
      // Best-effort, and silent: a failure here is already an `AppError` that logged itself.
      await AuthApplication.logout({ session }).catch(() => undefined);
      Logger.warn('Session upgrade approved with a different key');
      return false;
    }

    // The same boundary sign-in and restore apply: the key may have republished to a homeserver this
    // deployment refuses since the session was minted. Not signed out on failure — the cookie is
    // keyed by pubky, so signing this one out would leave the user with none.
    await AuthApplication.assertUserHomeserverAllowed({ publicKey: session.info.publicKey });

    // That check is a network round trip, so the account can change while it runs. Re-read the store
    // instead of using the snapshot above: storing now would resurrect a session after a sign-out, or
    // pair the new account with the previous one's session.
    const current = useAuthStore.getState();
    if (current.currentUserPubky !== pubky) {
      Logger.warn('Discarded an upgraded session: the account changed while it was being checked');
      return false;
    }

    if (current.storedSessionId) {
      await AuthApplication.removeStoredSession(current.storedSessionId).catch(() => undefined);
      if (useAuthStore.getState().currentUserPubky !== pubky) return false;
      current.setStoredSessionId(null);
    }
    current.setSession(session);
    return true;
  }

  /**
   * Session initialization shared by all sign-in flows; assumes the environment
   * guard already passed for this session.
   */
  private static async completeAuthenticatedSession(
    { session }: THomeserverSessionResult,
    storedSessionId?: string,
    passportInstance?: string,
  ) {
    this.cancelModerationFollow();
    const signInStore = useSignInStore.getState();
    signInStore.reset(); // Reset for fresh sign-in
    signInStore.setAuthUrlResolved(true); // Step 1 complete (20%)

    const authStore = useAuthStore.getState();

    try {
      this.cancelActiveAuthFlow();
      const pubky = Identity.z32FromSession({ session });

      authStore.init({ session, currentUserPubky: pubky, hasProfile: null });
      if (storedSessionId) authStore.setStoredSessionId(storedSessionId);
      if (passportInstance) authStore.setPassportInstance(passportInstance);

      const isSignedUp = await AuthApplication.userIsSignedUp({ pubky });
      signInStore.setProfileChecked(true); // Step 2 complete (40%)

      if (isSignedUp) {
        await this.hydrateMeImAlive({ pubky });
      }

      // Update hasProfile after bootstrap completes - triggers redirect via useAuthStatus
      authStore.setHasProfile(isSignedUp);
    } catch (error) {
      authStore.reset();
      signInStore.reset();
      throw error;
    }
  }

  /**
   * Signs up a new user with the homeserver using the provided secret key and signup token.
   * @param params - Object containing secret key and signup token for registration
   * @param params.secretKey - The secret key for the user
   * @param params.signupToken - Invitation code for user registration
   */
  static async signUp({ secretKey, signupToken }: TSignUpParams) {
    this.cancelModerationFollow();
    // Clear query clients to ensure no stale cache from previous session
    clearAllQueryClients();
    // Clear database before sign up to ensure clean state
    await clearDatabase();
    // Skip post-migration resync — new user has no homeserver data to resync
    useMigrationStore.getState().reset();
    // Settings are account-local: start from defaults so a previous account's document is never pushed to this one
    useSettingsStore.getState().reset();
    const keypair = Identity.keypairFromSecretKey(secretKey);
    const { session } = await AuthApplication.signUp({ keypair, signupToken });
    const authStore = useAuthStore.getState();
    const initialState = { session, currentUserPubky: Identity.z32FromSession({ session }), hasProfile: false };
    authStore.init(initialState);
  }

  /**
   * Authenticates the keypair with the homeserver and saves the authenticated data if successful.
   * @param params - Object containing the mnemonic phrase for key derivation
   * @param params.mnemonic - The mnemonic phrase for key derivation
   * @returns Promise resolving to true if authentication succeeded, false otherwise
   */
  static async loginWithMnemonic({ mnemonic }: TLoginWithMnemonicParams): Promise<boolean> {
    const keypair = Identity.keypairFromMnemonic(mnemonic);
    return await this.signIn({ keypair });
  }

  /**
   * Decrypts the file to obtain the keypair, authenticates with the homeserver, and saves authenticated data if successful.
   * @param params - Object containing the encrypted file and password for decryption
   * @param params.encryptedFile - The encrypted recovery file
   * @param params.password - The password to decrypt the recovery file
   * @returns Promise resolving to true if authentication succeeded, false otherwise
   */
  static async loginWithEncryptedFile({ encryptedFile, password }: TLoginWithEncryptedFileParams): Promise<boolean> {
    const keypair = await Identity.decryptRecoveryFile({ encryptedFile, passphrase: password });
    return await this.signIn({ keypair });
  }

  /**
   * Wraps sign-in URL generation: clears the previous account's local state, then tracks the flow.
   *
   * Ownership is taken synchronously, before the first `await`: a start that is superseded while
   * its database cleanup or URL generation is still in flight never becomes the active flow, never
   * cancels the newer flow, and rejects with the canceled error instead of returning a dead URL.
   * @param generateFn - Async function that returns the auth URL result
   * @returns Promise resolving to the generated authentication URL with wrapped approval
   */
  private static async wrapAuthFlow(
    generateFn: () => Promise<TGenerateAuthUrlResult>,
  ): Promise<TGenerateAuthUrlResult> {
    const token = this.claimAuthFlow();
    this.cancelModerationFollow();

    await clearDatabase();
    if (!this.ownsAuthFlow(token)) throw createCanceledError();

    // Skip post-migration resync — full bootstrap below covers all data
    useMigrationStore.getState().reset();
    // Settings are account-local: start from defaults so a previous account's document is never pushed to this one
    useSettingsStore.getState().reset();
    return this.trackAuthFlow(token, generateFn);
  }

  /** Synchronous on purpose: callers claim the flow before their first `await`. */
  private static claimAuthFlow(): symbol {
    const token = Symbol('auth-flow');
    this.cancelActiveAuthFlow();
    this.activeAuthFlow = { token, cancel: null };
    return token;
  }

  /**
   * Flow tracking only, so useAuthUrl can cancel on unmount and stale requests are detected
   * (e.g. React StrictMode double-mount, or a Pubky Ring request started right before a Passport
   * request). No local state is touched: the session upgrade runs this for a user who stays signed in.
   */
  private static async trackAuthFlow(
    token: symbol,
    generateFn: () => Promise<TGenerateAuthUrlResult>,
  ): Promise<TGenerateAuthUrlResult> {
    const { authorizationUrl, awaitApproval, cancelAuthFlow } = await generateFn();

    const activeAuthFlow = this.activeAuthFlow;
    if (!activeAuthFlow || activeAuthFlow.token !== token) {
      cancelAuthFlow();
      // Swallow the rejection of the now-orphaned approval so it never surfaces as unhandled.
      awaitApproval.catch(() => undefined);
      throw createCanceledError();
    }

    activeAuthFlow.cancel = cancelAuthFlow;

    const wrappedAwaitApproval = awaitApproval.finally(() => {
      if (this.activeAuthFlow?.token === token) {
        this.activeAuthFlow = null;
      }
      cancelAuthFlow();
    });

    return { authorizationUrl, awaitApproval: wrappedAwaitApproval, cancelAuthFlow };
  }

  /**
   * Centralizes all local state cleanup: resets every Zustand store, clears cookies,
   * IndexedDB, query cache, singletons, in-memory stream pagination queues, persisted localStorage keys,
   * and mute-sync `sessionStorage` cursors.
   * Used by both logout() and restorePersistedSession() on failure.
   */
  private static async cleanupLocalState() {
    this.cancelModerationFollow();
    // Mute-list SSE cursors live in sessionStorage; clear before the next account might reuse the same tab.
    clearMuteSyncCursorSessionStorage();

    // Reset singletons
    PubkySpecsSingleton.reset();
    // CoordinatorManager owns TTL lifetime; its auth listener clears session work.
    StreamCoordinator.resetInstance();
    NotificationCoordinator.resetInstance();

    // Clear in-memory feed stream queues
    postStreamQueue.clear();

    // Cancel active auth flows
    this.cancelActiveAuthFlow();

    // Cancel and clear all query clients (nexus, homegate, exchangerate, and any future ones)
    clearAllQueryClients();

    const storedSessionId = useAuthStore.getState().storedSessionId;
    if (storedSessionId) await AuthApplication.removeStoredSession(storedSessionId).catch(() => undefined);

    // Reset all Zustand stores.
    useOnboardingStore.getState().reset();
    useAuthStore.getState().reset();
    useSignInStore.getState().reset();
    useLocalFilesStore.getState().reset();
    useHomeStore.getState().reset();
    useHotStore.getState().reset();
    useSearchStore.getState().reset();
    useNotificationStore.getState().reset();
    useSettingsStore.getState().reset();

    // Unified logout: tear down the Locks session (Lock Server signout + local store) alongside the
    // homeserver session, so the user can never stay logged into Locks after leaving pubky.app.
    await LocksController.logout();

    // Clear cookies (also drops any stale `locale` cookie from the removed language selection)
    clearCookies();

    await clearDatabase();
    // Skip post-migration resync — full cleanup resets all state
    useMigrationStore.getState().reset();
  }

  /**
   * Generates an authentication URL for external authentication flows.
   * @returns Promise resolving to the generated authentication URL
   */
  static async getAuthUrl(): Promise<TGenerateAuthUrlResult> {
    return this.wrapAuthFlow(() => AuthApplication.generateAuthUrl());
  }

  /**
   * Generates a signup authentication URL for Pubky Ring authorization.
   * Decorates a standard auth URL with homeserver address and invite code metadata.
   * @param inviteCode - The invite code for signup
   * @returns Promise resolving to the generated signup authentication URL
   */
  static async getSignupAuthUrl(inviteCode: string): Promise<TGenerateAuthUrlResult> {
    return this.wrapAuthFlow(() => AuthApplication.generateSignupAuthUrl(inviteCode));
  }

  /**
   * Same Ring flow as sign-in (the requested list is already the current one), but for a signed-in
   * user (#2373): the local database and settings stay as they are. Approval goes to `upgradeSession`.
   */
  static async getUpgradeAuthUrl(): Promise<TGenerateAuthUrlResult> {
    return this.trackAuthFlow(this.claimAuthFlow(), () => AuthApplication.generateAuthUrl());
  }

  /**
   * Logs out the current user from both the homeserver and local application state.
   */
  static async logout() {
    this.cancelModerationFollow();
    const authStore = useAuthStore.getState();

    // Set logging out flag immediately to prevent flash of weird states in UI
    authStore.setIsLoggingOut(true);

    let session = authStore.session;

    // Fresh loads can still have a persisted session export before the live session is restored.
    // Restore credentials directly: revocation must not depend on profile or bootstrap reads.
    if (!session && (authStore.sessionExport || authStore.storedSessionId)) {
      try {
        session = (await AuthApplication.restorePersistedSession({ authStore }))?.session ?? null;
      } catch (error) {
        // A wrong-environment rejection needs no toast here — the user asked to log out anyway.
        Logger.warn('Persisted session restore during logout failed; clearing local state', { error });
      }
    }

    if (session) {
      // Bound the sign-out. The SDK's session.signout() takes no timeout or AbortSignal, so a
      // homeserver that accepts the connection and then never replies would hold logout open
      // until the OS-level TCP timeout, leaving cookies and the local database in place.
      // Local cleanup must always run; ending the server session is best-effort.
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      try {
        const timeoutPromise = new Promise<boolean>((resolve) => {
          timeoutId = setTimeout(() => resolve(true), LOGOUT_TIMEOUT_MS);
        });

        const timedOut = await Promise.race([
          AuthApplication.logout({ session }).then(
            () => false,
            (error) => {
              Logger.warn('Homeserver logout failed, clearing local state anyway', { error });
              return false;
            },
          ),
          timeoutPromise,
        ]);

        if (timedOut) {
          Logger.warn('Homeserver sign-out did not answer in time, clearing local state anyway', {
            timeoutMs: LOGOUT_TIMEOUT_MS,
          });
        }
      } finally {
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
        }
      }
    }

    await this.cleanupLocalState();
  }

  /**
   * Initializes the application bootstrap after profile creation.
   * Waits for Nexus to index the user's profile.json, then bootstraps notifications and data.
   */
  static async bootstrapWithDelay() {
    const authStore = useAuthStore.getState();
    const pubky = authStore.selectCurrentUserPubky();
    // Wait 5 seconds before bootstrap to let Nexus index the user
    Logger.info(`Waiting 5 seconds to index ${pubky} profile.json in Nexus before bootstrap...`);
    await sleep(5000);
    await this.hydrateMeImAlive({ pubky });
    authStore.setHasProfile(true);
  }

  /**
   * Syncs user settings with the homeserver.
   * Returns remote settings if newer, null otherwise, plus whether synchronization succeeded.
   * Failures are non-blocking but suppress moderation follow for this session because opt-out state is unknown.
   */
  private static async syncSettings(
    pubky: Pubky,
    localSettings: SettingsState,
  ): Promise<{ remoteSettings: SettingsState | null; succeeded: boolean }> {
    try {
      return {
        remoteSettings: await SettingsApplication.initializeSettings(pubky, localSettings),
        succeeded: true,
      };
    } catch (error) {
      Logger.error('Failed to initialize settings during bootstrap', { error, pubky });
      return { remoteSettings: null, succeeded: false };
    }
  }

  /**
   * Generates a signup token for user registration. This is just for testing environments
   * @returns Promise resolving to the generated signup token
   */
  static async generateSignupToken() {
    return await AuthApplication.generateSignupToken();
  }

  /**
   * Verifies an invite code (signup token) against the homeserver before applying it.
   * @param inviteCode - The invite code to verify
   * @returns `'valid'`, `'used'`, or `'invalid'` depending on the homeserver response
   */
  static async verifySignupToken(inviteCode: string) {
    return await AuthApplication.verifySignupToken(inviteCode);
  }
}
